import React from 'react';
import {
    Alert,
    IconButton,
    Link,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    TablePagination,
    Tooltip,
    Typography,
} from '@mui/material';
import PauseCircleOutlineRoundedIcon from '@mui/icons-material/PauseCircleOutlineRounded';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import useSourcesUploadQueueStore from './useSourcesUploadQueueStore';
import useSourcesUploadQueueUiStore from './useSourcesUploadQueueUiStore';
import {parseApiDateTime} from './sourcesFileMetadata';

/** Human-readable label for a pipeline stage name from meta.processing.stage */
const STAGE_LABELS = {
    STORING_RAW: 'Storing…',
    QUEUED: 'Queued',
    PDF_EXTRACTING: 'Extracting PDF…',
    AUDIO_TRANSCRIBING: 'Transcribing…',
    IMAGE_DESCRIBING: 'Describing image…',
    TEXT_EXTRACTING: 'Extracting text…',
    BUILDING_ORIGINAL_PM: 'Building document…',
    LANGUAGE_DETECTING: 'Detecting language…',
    TRANSLATING: 'Translating…',
    ENRICHING: 'Enriching…',
    EMBEDDING: 'Embedding…',
    DONE: 'Done',
    FAILED: 'Failed',
};

function formatBytes(value) {
    const size = Number(value || 0);
    if (!size) {
        return '0 B';
    }

    if (size < 1024) {
        return `${size} B`;
    }

    const units = ['KB', 'MB', 'GB'];
    let current = size / 1024;
    let unitIndex = 0;
    while (current >= 1024 && unitIndex < units.length - 1) {
        current /= 1024;
        unitIndex += 1;
    }

    return `${current.toFixed(1)} ${units[unitIndex]}`;
}

function isWebImportItem(item) {
    return item?.sourceKind === 'url' || item?.sourceKind === 'paste';
}

function formatQueueItemStatus(item) {
    const imported = isWebImportItem(item);
    if (item.status === 'duplicate') {
        return 'Already in My files';
    }
    if (item.status === 'failed') {
        return 'Failed';
    }
    if (item.status === 'success') {
        return imported ? 'Imported' : 'Uploaded';
    }
    if (item.status === 'cancelled') {
        return 'Canceled';
    }
    if (item.status === 'processing') {
        return 'Processing';
    }
    return item.status;
}

function formatRelativeTime(ts, nowMs = Date.now()) {
    const date = parseApiDateTime(ts);
    if (!date) {
        return null;
    }

    const diffMs = Math.max(0, nowMs - date.getTime());
    const diffSecs = Math.floor(diffMs / 1000);
    if (diffSecs < 60) {
        return 'just now';
    }

    const diffMins = Math.floor(diffSecs / 60);
    if (diffMins < 60) {
        return `${diffMins} min ago`;
    }

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) {
        return `${diffHours} hr ago`;
    }

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) {
        return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }

    const diffMonths = Math.floor(diffDays / 30);
    return `${diffMonths} mo ago`;
}

function getQueueItemReferenceTime(item) {
    if (item.finishedAt && ['success', 'failed', 'duplicate', 'cancelled'].includes(item.status)) {
        return item.finishedAt;
    }
    if (item.startedAt && ['uploading', 'processing'].includes(item.status)) {
        return item.startedAt;
    }
    return item.createdAt;
}

function formatQueueItemSecondary(item, nowMs = Date.now()) {
    const parts = [formatQueueItemStatus(item)];
    if (item.error && item.status !== 'uploading' && item.status !== 'queued' && item.status !== 'processing') {
        parts.push(item.error);
    } else if (item.status === 'uploading' || item.status === 'queued' || item.status === 'processing') {
        // Server-hydrated items carry stage + progressPercent; local XHR items carry progress %.
        if (item.stage && STAGE_LABELS[item.stage]) {
            const pct = item.progressPercent != null ? item.progressPercent : item.progress;
            parts.push(`${STAGE_LABELS[item.stage]} ${Math.round(pct || 0)}%`);
        } else {
            parts.push(`${Math.round(item.progress || 0)}%`);
        }
    }
    // Web imports have no byte size; only show size for local file uploads.
    if (!isWebImportItem(item)) {
        parts.push(formatBytes(item.size));
    }
    const relativeTime = formatRelativeTime(getQueueItemReferenceTime(item), nowMs);
    if (relativeTime) {
        parts.push(relativeTime);
    }
    return parts.join(' • ');
}

/** True when the item came from server hydration (File blob unavailable). */
function isServerHydrated(item) {
    return item?.fileKey === null && item?.uploadedFileId != null;
}

function sortQueueItemsNewestFirst(items) {
    return [...items].sort((a, b) => {
        const aTime = parseApiDateTime(a.createdAt)?.getTime() || 0;
        const bTime = parseApiDateTime(b.createdAt)?.getTime() || 0;
        return bTime - aTime;
    });
}

const QUEUE_PAGE_SIZE = 25;

const SourcesUploadQueue = ({onCancelItem, onClearQueue, onOpenExistingFile, onOpenFile}) => {
    const items = useSourcesUploadQueueStore((state) => state.items);
    const displayItems = React.useMemo(() => sortQueueItemsNewestFirst(items), [items]);
    const [page, setPage] = React.useState(0);
    const pageCount = Math.max(1, Math.ceil(displayItems.length / QUEUE_PAGE_SIZE));
    const safePage = Math.min(page, pageCount - 1);
    const pagedItems = React.useMemo(
        () => displayItems.slice(safePage * QUEUE_PAGE_SIZE, (safePage + 1) * QUEUE_PAGE_SIZE),
        [displayItems, safePage],
    );

    React.useEffect(() => {
        if (page !== safePage) {
            setPage(safePage);
        }
    }, [page, safePage]);
    const paused = useSourcesUploadQueueStore((state) => state.paused);
    const pauseAll = useSourcesUploadQueueStore((state) => state.pauseAll);
    const resumeAll = useSourcesUploadQueueStore((state) => state.resumeAll);
    const retryItem = useSourcesUploadQueueStore((state) => state.retryItem);
    const clearCompleted = useSourcesUploadQueueStore((state) => state.clearCompleted);

    const summary = React.useMemo(() => {
        const total = items.length;
        const counts = items.reduce((acc, item) => {
            acc[item.status] = (acc[item.status] || 0) + 1;
            return acc;
        }, {});
        const progressTotal = items.reduce((acc, item) => acc + Number(item.progress || 0), 0);

        return {
            total,
            uploading: counts.uploading || 0,
            processing: counts.processing || 0,
            queued: counts.queued || 0,
            paused: counts.paused || 0,
            success: counts.success || 0,
            failed: counts.failed || 0,
            duplicate: counts.duplicate || 0,
            cancelled: counts.cancelled || 0,
            progress: total ? Math.round(progressTotal / total) : 0,
        };
    }, [items]);

    const hasItems = summary.total > 0;
    const hasActiveUploads = summary.uploading > 0 || summary.queued > 0 || summary.paused > 0 || summary.processing > 0;

    const [, setRelativeTimeTick] = React.useState(0);
    React.useEffect(() => {
        if (!hasItems) {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            setRelativeTimeTick((tick) => tick + 1);
        }, 30000);

        return () => window.clearInterval(intervalId);
    }, [hasItems]);

    const duplicateItems = React.useMemo(
        () => sortQueueItemsNewestFirst(items.filter((item) => item.status === 'duplicate')),
        [items],
    );

    const openExistingFile = React.useCallback((item) => {
        if (!item?.existingReferenceId || typeof onOpenExistingFile !== 'function') {
            return;
        }
        onOpenExistingFile({id: item.existingReferenceId, name: item.name});
    }, [onOpenExistingFile]);

    // Collapsed/dismissed state is in-memory; resets to defaults on page load.
    const isCollapsed = useSourcesUploadQueueUiStore((state) => state.collapsed);
    const isDismissed = useSourcesUploadQueueUiStore((state) => state.dismissed);
    const toggleCollapsed = useSourcesUploadQueueUiStore((state) => state.toggleCollapsed);
    const setCollapsed = useSourcesUploadQueueUiStore((state) => state.setCollapsed);
    const setDismissed = useSourcesUploadQueueUiStore((state) => state.setDismissed);

    // Re-show the queue for live activity (active uploads always un-dismiss). For duplicates,
    // only un-dismiss when a NEW duplicate appears this session, not on reload of persisted ones.
    const prevDuplicateRef = React.useRef(null);
    React.useEffect(() => {
        const prev = prevDuplicateRef.current;
        prevDuplicateRef.current = summary.duplicate;

        if (hasActiveUploads) {
            setDismissed(false);
            return;
        }

        if (prev === null) {
            return;
        }

        if (summary.duplicate > prev) {
            setDismissed(false);
            setCollapsed(false);
        }
    }, [hasActiveUploads, summary.duplicate, setDismissed, setCollapsed]);

    if (!hasItems || (isDismissed && !hasActiveUploads)) {
        return null;
    }

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 1.5,
                borderRadius: 0,
                bgcolor: 'background.paper',
            }}
        >
            <Stack spacing={1}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body2" fontWeight={600}>
                        Upload Queue
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip title={isCollapsed ? 'Expand queue' : 'Collapse queue'}>
                            <span>
                                <IconButton size="small" onClick={toggleCollapsed}>
                                    {isCollapsed ? <ExpandMoreRoundedIcon fontSize="small"/> :
                                        <ExpandLessRoundedIcon fontSize="small"/>}
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={paused ? 'Resume uploads' : 'Pause uploads'}>
                            <span>
                                <IconButton size="small" onClick={paused ? resumeAll : pauseAll}
                                            disabled={!summary.total}>
                                    {paused ? <PlayCircleOutlineRoundedIcon fontSize="small"/> :
                                        <PauseCircleOutlineRoundedIcon fontSize="small"/>}
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Clear completed">
                            <span>
                                <IconButton size="small" onClick={clearCompleted}
                                            disabled={!summary.success && !summary.cancelled && !summary.duplicate}>
                                    <DoneAllRoundedIcon fontSize="small"/>
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Clear queue">
                            <span>
                                <IconButton size="small" onClick={onClearQueue} disabled={!summary.total}>
                                    <DeleteSweepRoundedIcon fontSize="small"/>
                                </IconButton>
                            </span>
                        </Tooltip>
                        {!hasActiveUploads && (
                            <Tooltip title="Close queue">
                                <span>
                                    <IconButton size="small" onClick={() => setDismissed(true)}>
                                        <CloseRoundedIcon fontSize="small"/>
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Stack>
                </Stack>

                <Typography variant="caption" color="text.secondary">
                    {summary.success} uploaded
                    {summary.duplicate > 0 ? `, ${summary.duplicate} already in My files` : ''}
                    {summary.failed > 0 ? `, ${summary.failed} failed` : ''}
                    {summary.uploading > 0 ? `, ${summary.uploading} uploading` : ''}
                    {summary.processing > 0 ? `, ${summary.processing} processing` : ''}
                    {summary.queued > 0 ? `, ${summary.queued} queued` : ''}
                </Typography>

                <LinearProgress variant="determinate" value={summary.progress}/>

                {!isCollapsed && summary.duplicate > 0 && (
                    <Alert severity="info" sx={{py: 0}}>
                        {summary.duplicate} file{summary.duplicate === 1 ? '' : 's'} skipped because{' '}
                        {summary.duplicate === 1 ? 'it already exists' : 'they already exist'} in My files.
                        {duplicateItems.some((item) => item.existingReferenceId) && (
                            <Stack component="span" sx={{display: 'block', mt: 0.5}}>
                                {duplicateItems.map((item) => (
                                    item.existingReferenceId ? (
                                        <Link
                                            key={item.id}
                                            component="button"
                                            type="button"
                                            variant="caption"
                                            underline="hover"
                                            onClick={() => openExistingFile(item)}
                                            sx={{
                                                display: 'block',
                                                textAlign: 'left',
                                                maxWidth: '100%',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {item.name}
                                        </Link>
                                    ) : null
                                ))}
                            </Stack>
                        )}
                    </Alert>
                )}

                {!isCollapsed && summary.failed > 0 && (
                    <Alert severity="warning" sx={{py: 0}}>
                        {summary.failed} upload(s) failed. Retry each failed file from the queue.
                    </Alert>
                )}

                {!isCollapsed && (
                    <List dense sx={{maxHeight: 200, overflowY: 'auto', p: 0}}>
                        {pagedItems.map((item) => (
                            <ListItem
                                key={item.id}
                                disableGutters
                                secondaryAction={(
                                    <Stack direction="row" spacing={0.5}>
                                        {/* Local in-flight items: show Cancel */}
                                        {!isServerHydrated(item) && (item.status === 'queued' || item.status === 'uploading' || item.status === 'paused') && (
                                            <Tooltip title="Cancel">
                                                <IconButton size="small" onClick={() => onCancelItem(item.id)}>
                                                    <CancelOutlinedIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {/* Local failed/cancelled items: show Retry */}
                                        {!isServerHydrated(item) && (item.status === 'failed' || item.status === 'cancelled') && (
                                            <Tooltip title="Retry">
                                                <IconButton size="small" onClick={() => retryItem(item.id)}>
                                                    <ReplayRoundedIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {/* Server-hydrated success: show "View file" link */}
                                        {isServerHydrated(item) && item.status === 'success' && typeof onOpenFile === 'function' && (
                                            <Tooltip title="View file">
                                                <IconButton size="small" onClick={() => onOpenFile({id: item.uploadedFileId, name: item.name})}>
                                                    <OpenInNewRoundedIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                )}
                            >
                                <ListItemText
                                    primaryTypographyProps={{
                                        variant: 'body2',
                                        noWrap: true,
                                        color: item.status === 'duplicate' ? 'warning.main' : undefined,
                                        component: 'div',
                                    }}
                                    secondaryTypographyProps={{variant: 'caption'}}
                                    primary={isWebImportItem(item) ? (
                                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{minWidth: 0}}>
                                            <PublicRoundedIcon
                                                fontSize="inherit"
                                                color="action"
                                                aria-label="imported from web"
                                                sx={{flexShrink: 0}}
                                            />
                                            <Typography variant="body2" noWrap component="span">
                                                {item.name}
                                            </Typography>
                                        </Stack>
                                    ) : (item.status === 'duplicate' && item.existingReferenceId) ? (
                                        <Link
                                            component="button"
                                            type="button"
                                            variant="body2"
                                            underline="hover"
                                            title="Open the existing file in My files"
                                            onClick={() => openExistingFile(item)}
                                            sx={{
                                                display: 'block',
                                                textAlign: 'left',
                                                maxWidth: '100%',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {item.name}
                                        </Link>
                                    ) : item.name}
                                    secondary={formatQueueItemSecondary(item)}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}

                {!isCollapsed && displayItems.length > QUEUE_PAGE_SIZE && (
                    <TablePagination
                        component="div"
                        count={displayItems.length}
                        page={safePage}
                        onPageChange={(_, nextPage) => setPage(nextPage)}
                        rowsPerPage={QUEUE_PAGE_SIZE}
                        rowsPerPageOptions={[QUEUE_PAGE_SIZE]}
                        labelRowsPerPage=""
                    />
                )}
            </Stack>
        </Paper>
    );
};

export default React.memo(SourcesUploadQueue);
